import React from 'react';
import { 
  Chart as ChartJS, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  BarElement, 
  Title,
  Filler
} from 'chart.js';
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import { Paper, Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';

// Registrar componentes do Chart.js
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  BarElement, 
  Title,
  Filler
);

// Tipos de gráficos disponíveis
export type ChartType = 'pie' | 'bar' | 'line' | 'doughnut';

// Interface para as propriedades do componente
interface ChartComponentProps {
  chartType: ChartType;
  chartData: any;
  chartOptions?: any;
  title: string;
  loading?: boolean;
  error?: string | null;
  height?: number | string;
  /** Sem Paper/título — para embutir em um card externo. */
  embedded?: boolean;
}

const ChartComponent: React.FC<ChartComponentProps> = ({
  chartType,
  chartData,
  chartOptions = {},
  title,
  loading = false,
  error = null,
  height = 300,
  embedded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Em modo embutido, respeita a altura pedida (evita estourar o layout no mobile)
  const adjustedHeight = embedded
    ? height
    : isMobile
      ? typeof height === 'number'
        ? Math.max(height, 450)
        : '450px'
      : height;

  const Wrapper: React.ElementType = embedded ? Box : Paper;
  const paperProps = embedded ? {} : { elevation: 3 };
  const wrapperSx = embedded
    ? { height: adjustedHeight, minHeight: typeof adjustedHeight === 'number' ? adjustedHeight : undefined }
    : { p: 2, height: adjustedHeight, minHeight: isMobile ? '450px' : '300px' };
  
  // Configurações ajustadas para móvel
  const adjustedOptions = {
    ...chartOptions,
    maintainAspectRatio: false,
    responsive: true,
    // Ajustar o tamanho da fonte da legenda em dispositivos móveis
    plugins: {
      ...chartOptions.plugins,
      legend: {
        ...chartOptions.plugins?.legend,
        labels: {
          ...chartOptions.plugins?.legend?.labels,
          font: {
            ...chartOptions.plugins?.legend?.labels?.font,
            size: isMobile ? 12 : chartOptions.plugins?.legend?.labels?.font?.size || 14,
          },
          boxWidth: isMobile ? 12 : chartOptions.plugins?.legend?.labels?.boxWidth || 15,
        },
      },
    },
  };

  const contentHeight = embedded || !title ? '100%' : 'calc(100% - 40px)';

  // Renderizar carregamento
  if (loading) {
    return (
      <Wrapper {...paperProps} sx={wrapperSx}>
        {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: contentHeight }}>
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  // Renderizar mensagem de erro
  if (error) {
    return (
      <Wrapper {...paperProps} sx={wrapperSx}>
        {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: contentHeight }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Wrapper>
    );
  }

  // Renderizar gráfico vazio se não houver dados
  const hasData = chartData && 
                  chartData.datasets && 
                  chartData.datasets.length > 0 && 
                  chartData.datasets[0].data && 
                  chartData.datasets[0].data.length > 0;

  if (!hasData) {
    return (
      <Wrapper {...paperProps} sx={wrapperSx}>
        {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: contentHeight }}>
          <Typography color="text.secondary">Sem dados disponíveis</Typography>
        </Box>
      </Wrapper>
    );
  }

  // Renderizar o tipo de gráfico correto
  const renderChart = () => {
    switch (chartType) {
      case 'pie':
        return <Pie data={chartData} options={adjustedOptions} />;
      case 'bar':
        return <Bar data={chartData} options={adjustedOptions} />;
      case 'line':
        return <Line data={chartData} options={adjustedOptions} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={adjustedOptions} />;
      default:
        return <Bar data={chartData} options={adjustedOptions} />;
    }
  };

  return (
    <Wrapper {...paperProps} sx={wrapperSx}>
      {!embedded && title ? <Typography variant="h6" gutterBottom>{title}</Typography> : null}
      <Box sx={{ height: contentHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {renderChart()}
      </Box>
    </Wrapper>
  );
};

export default ChartComponent; 